import type { Id } from '../../convex/_generated/dataModel';
import type { PropertyVideoWizardStep, PropertyWizardFlow } from '@/@types/property';
import type { IFullVideoGeneratorState } from '@/@types/fullVideoGenerator';
import { PropertyUrlInput } from '@/components/property/PropertyUrlInput';
import { PropertyDataReview } from '@/components/property/PropertyDataReview';
import { PropertyNearbyPlaces } from '@/components/property/PropertyNearbyPlaces';
import { ImageProcessingStep } from '@/components/property/ImageProcessingStep';
import { PropertySceneEditorMemo } from '@/components/property/PropertySceneEditor';
import { WizardNavigationBar } from '@/components/property/WizardNavigationBar';
import { FinalVideoGeneration } from '@/components/property/FinalVideoGeneration';
import {
  ScrapingLoadingState,
  ErrorDisplay,
} from '@/components/property/PropertyVideoWizardComponents';
import type { IPropertyListing } from '@/@types/property';

interface IPropertyVideoStepContentProps {
  currentStep: PropertyVideoWizardStep;
  wizardFlow: PropertyWizardFlow;
  property: IPropertyListing | null | undefined;
  propertyId: Id<'propertyListings'> | undefined;
  currentVideoId: Id<'propertyVideos'> | null;
  isScrapingInProgress: boolean;
  isScrapingFailed: boolean;
  isScraping: boolean;
  isAnalyzing: boolean;
  isGenerating: boolean;
  isReady: boolean;
  scrapingStatus: string | null;
  scrapingErrorMessage: string | null | undefined;
  isCreatingVideo: boolean;
  startError: string | null;
  selectedVoiceId: string;
  selectedDuration: number;
  selectedAspectRatio: '16:9' | '9:16';
  fullVideoGeneratorState: IFullVideoGeneratorState;
  propertyVideo:
    | { finalVideoId?: Id<'_storage'>; durationSeconds?: number; fileSizeBytes?: number }
    | null
    | undefined;
  finalVideoUrl: string | null | undefined;
  getDisplayStep: (internalStep: number) => PropertyVideoWizardStep;
  onUrlSubmit: (url: string) => Promise<void>;
  onRetry: () => void;
  onDataReviewContinue: () => void;
  onGoBack: () => void;
  onNearbyPlacesContinue: () => void;
  onImageProcessingContinue: (aspectRatio: '16:9' | '9:16') => Promise<void>;
  onImageProcessingBack: () => void;
  onClearError: () => void;
  onVoiceChange: (voiceId: string) => void;
  onDurationChange: (duration: number) => void;
  onAspectRatioChange: (ratio: '16:9' | '9:16') => void;
  onGenerateFullVideo: () => Promise<void>;
  onResetFullVideoGenerator: () => void;
  dispatchSetStep: (step: PropertyVideoWizardStep) => void;
}

export function PropertyVideoStepContent({
  currentStep,
  wizardFlow,
  property,
  currentVideoId,
  isScrapingInProgress,
  isScrapingFailed,
  isScraping,
  isAnalyzing,
  isGenerating,
  isReady,
  scrapingStatus,
  scrapingErrorMessage,
  isCreatingVideo,
  startError,
  selectedVoiceId,
  selectedDuration,
  selectedAspectRatio,
  fullVideoGeneratorState,
  propertyVideo,
  finalVideoUrl,
  propertyId,
  getDisplayStep,
  onUrlSubmit,
  onRetry,
  onDataReviewContinue,
  onGoBack,
  onNearbyPlacesContinue,
  onImageProcessingContinue,
  onImageProcessingBack,
  onClearError,
  onVoiceChange,
  onDurationChange,
  onAspectRatioChange,
  onGenerateFullVideo,
  onResetFullVideoGenerator,
  dispatchSetStep,
}: IPropertyVideoStepContentProps) {
  if (currentStep === 1) {
    return (
      <PropertyUrlInput
        onSubmit={onUrlSubmit}
        isLoading={isScrapingInProgress}
        error={startError}
      />
    );
  }

  if (currentStep === 2) {
    if (isScrapingFailed && scrapingErrorMessage) {
      return <ErrorDisplay message={scrapingErrorMessage} onRetry={onRetry} />;
    }
    if (isScraping || isAnalyzing || isGenerating) {
      return <ScrapingLoadingState status={scrapingStatus ?? 'scraping'} />;
    }
    if (property && isReady) {
      return (
        <div className="w-full max-w-2xl">
          <PropertyDataReview property={property} showNearbyPlaces={wizardFlow === 'draft'} />
          <WizardNavigationBar
            currentStep={getDisplayStep(currentStep)}
            onBack={wizardFlow === 'draft' ? undefined : onGoBack}
            onContinue={onDataReviewContinue}
          />
        </div>
      );
    }
    return <ScrapingLoadingState status="scraping" />;
  }

  if (currentStep === 3) {
    if (!property) {
      return <ScrapingLoadingState status="analyzing_location" />;
    }
    return (
      <div className="w-full max-w-2xl">
        <PropertyNearbyPlaces nearbyPlaces={property.nearbyPlaces} />
        <WizardNavigationBar
          currentStep={getDisplayStep(currentStep)}
          onBack={onGoBack}
          onContinue={onNearbyPlacesContinue}
        />
      </div>
    );
  }

  if (currentStep === 4) {
    if (!property) {
      return <ScrapingLoadingState status="scraping" />;
    }
    return (
      <ImageProcessingStep
        property={property}
        onContinue={onImageProcessingContinue}
        onBack={onImageProcessingBack}
        isContinueLoading={isCreatingVideo}
      />
    );
  }

  if (currentStep === 5) {
    if (startError) {
      return <ErrorDisplay message={startError} onRetry={onClearError} />;
    }
    if (!currentVideoId) {
      return (
        <ErrorDisplay
          message="Video not found. Please go back and try again."
          onRetry={() => dispatchSetStep(4 as PropertyVideoWizardStep)}
        />
      );
    }

    const isGen =
      fullVideoGeneratorState.status === 'downloading' ||
      fullVideoGeneratorState.status === 'concatenating' ||
      fullVideoGeneratorState.status === 'uploading';

    const hasExistingVideo = !!propertyVideo?.finalVideoId;

    return (
      <div className="w-full max-w-6xl">
        <PropertySceneEditorMemo
          videoId={currentVideoId}
          propertyId={propertyId!}
          selectedVoiceId={selectedVoiceId}
          selectedDuration={selectedDuration}
          selectedAspectRatio={selectedAspectRatio}
          onVoiceChange={onVoiceChange}
          onDurationChange={onDurationChange}
          onAspectRatioChange={onAspectRatioChange}
          onGenerateFullVideo={onGenerateFullVideo}
          isGenerating={isGen}
          hasExistingVideo={hasExistingVideo}
          onViewExistingVideo={() => dispatchSetStep(6 as PropertyVideoWizardStep)}
        />
        <WizardNavigationBar
          currentStep={getDisplayStep(currentStep)}
          onBack={() => dispatchSetStep(4 as PropertyVideoWizardStep)}
          backLabel="Back to Images"
        />
      </div>
    );
  }

  if (currentStep === 6) {
    return (
      <div className="w-full max-w-2xl">
        <FinalVideoGeneration
          generatorState={fullVideoGeneratorState}
          finalVideoUrl={finalVideoUrl}
          videoMetadata={
            propertyVideo?.durationSeconds && propertyVideo?.fileSizeBytes
              ? {
                  durationSeconds: propertyVideo.durationSeconds,
                  fileSizeBytes: propertyVideo.fileSizeBytes,
                }
              : undefined
          }
          onRegenerate={onGenerateFullVideo}
          onReset={onResetFullVideoGenerator}
        />
        <WizardNavigationBar
          currentStep={getDisplayStep(currentStep)}
          onBack={() => dispatchSetStep(5 as PropertyVideoWizardStep)}
          backLabel="Back to Settings"
        />
      </div>
    );
  }

  return null;
}
